import { useEffect, useMemo } from 'react';
import { normalizeBrandColors } from './buildBrandPalette';

const STORAGE_KEY = 'salovare.brandColors';

const readAll = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

export const readCachedBrandColors = (cacheKey) => (cacheKey ? normalizeBrandColors(readAll()[cacheKey]) : null);

export const writeCachedBrandColors = (cacheKey, colors) => {
  if (!cacheKey) return;
  try {
    const all = readAll();
    const normalized = normalizeBrandColors(colors);
    if (normalized) {
      all[cacheKey] = normalized;
    } else {
      delete all[cacheKey];
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {
    // Storage unavailable (private mode, quota): the cache is only a first-paint optimisation.
  }
};

/**
 * Returns the colours to theme with: the resolved ones once `resolved` is true
 * (and caches them), otherwise the last cached colours for `cacheKey`, so a
 * store's palette is painted before its data arrives.
 */
export const useCachedBrandColors = (cacheKey, colors, resolved) => {
  const { primary, accent, text, background } = normalizeBrandColors(colors) || {};

  useEffect(() => {
    if (resolved) writeCachedBrandColors(cacheKey, { primary, accent, text, background });
  }, [cacheKey, resolved, primary, accent, text, background]);

  return useMemo(
    () => (resolved
      ? normalizeBrandColors({ primary, accent, text, background })
      : readCachedBrandColors(cacheKey)),
    [cacheKey, resolved, primary, accent, text, background],
  );
};
